import { lazy, Suspense, useEffect, useState } from 'react';
import { riversideExample } from './example/riverside';
import { ProjectsScreen } from './projects/ProjectsScreen';
import { addEquipment, addSource, deleteSelection, duplicateSelection } from './state/actions';
import { db } from './state/db';
import { startCloudSync } from './state/cloudSync';
import { startAutosave } from './state/persistence';
import { useStore } from './state/store';
import { SignIn } from './auth/SignIn';
import { CommandPalette } from './workspace/CommandPalette';
import { Workspace } from './workspace/Workspace';

const Dossier = lazy(() => import('./report/Dossier').then((m) => ({ default: m.Dossier })));

const EXAMPLE_ID = 'example-riverside';
const typing = (t: EventTarget | null) => t instanceof HTMLElement && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName));

function useShortcuts() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const s = useStore.getState();
      if (!s.doc) return;
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      if (mod && key === 'k') { e.preventDefault(); s.setPalette(!s.paletteOpen); return; }
      if (s.paletteOpen) return;
      if (mod && key === 'z' && !typing(e.target)) { e.preventDefault(); if (e.shiftKey) s.redo(); else s.undo(); return; }
      if (mod && key === 'y' && !typing(e.target)) { e.preventDefault(); s.redo(); return; }
      if (mod && key === 'd' && !typing(e.target)) { e.preventDefault(); duplicateSelection(); return; }
      if (mod || e.altKey || typing(e.target)) return;
      switch (key) {
        case 'delete': case 'backspace': if (s.selection) { e.preventDefault(); deleteSelection(); } break;
        case 'escape': s.select(null); break;
        case 's': addSource(); break;
        case 'e': addEquipment(); break;
        case 'f': if (e.shiftKey) s.select(null); s.requestFocus(); break;
        case 'p': s.setView({ mode: 'plan' }); break;
        case 'l': s.setView({ mode: 'elev' }); break;
        case 'v': s.setView({ mode: 'persp' }); break;
        case '1': case '2': case '3': { const z = (Number(key) - 1) as 0 | 1 | 2; s.setView({ zones: { ...s.view.zones, [z]: !s.view.zones[z] } }); break; }
        case '0': { const all = Object.values(s.view.zones).every(Boolean); s.setView({ zones: { 0: !all, 1: !all, 2: !all } }); break; }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

export function App() {
  const projectId = useStore((s) => s.projectId);
  const screen = useStore((s) => s.screen);
  const signingIn = useStore((s) => s.signingIn);
  const [ready, setReady] = useState(false);
  useShortcuts();
  useEffect(() => {
    const stop = startAutosave();
    const stopCloud = startCloudSync();
    // Deep link for demos: #example opens the example project; &sel=<id> selects something in it.
    const hash = new URLSearchParams(window.location.hash.slice(1));
    if (hash.has('example')) {
      const doc = riversideExample();
      useStore.getState().openProject(EXAMPLE_ID, doc);
      void db.projects.put({ id: EXAMPLE_ID, name: doc.meta.name, updatedAt: Date.now(), doc }).catch(() => undefined);
      const sel = hash.get('sel');
      const kind = sel ? (doc.sources.some((x) => x.id === sel) ? 'source' : doc.equipment.some((x) => x.id === sel) ? 'equipment' : doc.areas.some((x) => x.id === sel) ? 'area' : null) : null;
      if (sel && kind) useStore.getState().select({ kind, id: sel });
      if (hash.has('tab')) useStore.getState().setView({ dockTab: hash.get('tab') as never });
      if (hash.has('view')) useStore.getState().setView({ mode: hash.get('view') as never });
      if (hash.has('dossier')) useStore.getState().setScreen('dossier');
      setReady(true);
      return () => { stop(); stopCloud(); };
    }
    let last: string | null = null;
    try { last = localStorage.getItem('extent:last'); } catch { /* storage blocked */ }
    (last ? db.projects.get(last) : Promise.resolve(undefined))
      .then((p) => { if (p) useStore.getState().openProject(p.id, p.doc); })
      .catch(() => undefined)
      .finally(() => setReady(true));
    return () => { stop(); stopCloud(); };
  }, []);
  useEffect(() => {
    try { if (projectId) localStorage.setItem('extent:last', projectId); else localStorage.removeItem('extent:last'); } catch { /* storage blocked */ }
  }, [projectId]);
  if (!ready) return null;
  if (signingIn) return <SignIn onBack={() => useStore.getState().setSigningIn(false)} />;
  if (!projectId) return <ProjectsScreen />;
  return (
    <>
      {screen === 'dossier' ? <Suspense fallback={null}><Dossier /></Suspense> : <Workspace />}
      <CommandPalette />
    </>
  );
}
